// Lightweight, dep-free chart primitives.
//
// Pure SVG so they render cleanly in the print / PDF popup without any
// runtime canvas / WebGL dependency.  None of these are meant to replace
// a real charting library — they're for the report dashboard's "tell the
// story at a glance" sparklines and breakdowns.
//
// All charts accept a `palette` array of hex colours; default rotates
// through the brand brown / gold so they stay on-theme.

import React from "react";

const BRAND_PALETTE = [
  "#c8a05a", "#2c1f14", "#a8814a", "#4a7c59", "#3a7bd5",
  "#e67e22", "#a93226", "#8a6a30", "#6b5142", "#2d6a4f",
];

// ── KPI tile ────────────────────────────────────────────────────────────
//   <StatTile label="Reservations" value={132} sub="last 30 days" />
//
// Compact-by-default so the report KPI rows fit 4–6 tiles on a single
// line without dominating the page.  The parent `.kpi-row` (see
// dashboard.css) is `display:flex; flex-wrap:nowrap` — each tile
// `flex:1 1 0; min-width:0` so they shrink uniformly, and on narrow
// viewports the whole row scrolls horizontally instead of stacking.
export function StatTile({ icon, label, value, sub, accent = "#c8a05a" }) {
  return (
    <div style={{
      padding: "10px 12px",
      borderRadius: 9,
      border: "1px solid rgba(180,140,80,.22)",
      background: "linear-gradient(180deg, #fdf8f2 0%, #fcf3e6 100%)",
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Top row: icon + label side-by-side keeps the tile compact. */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        {icon && <span style={{ fontSize: 14, opacity: .85, lineHeight: 1 }}>{icon}</span>}
        <span style={{
          fontSize: 10, letterSpacing: ".14em",
          textTransform: "uppercase", color: "#6b5142", fontWeight: 600,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {label}
        </span>
      </div>
      <div style={{
        fontFamily: "'Cormorant Garamond', serif",
        fontSize: 22, color: "#2c1f14", fontWeight: 600, lineHeight: 1.1,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>
        {value ?? "—"}
      </div>
      {sub && (
        <div style={{
          fontSize: 10.5, color: "#8a7060", marginTop: 3,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{sub}</div>
      )}
      <span style={{
        position: "absolute", top: 0, left: 0, bottom: 0, width: 3,
        background: accent,
      }}/>
    </div>
  );
}

// ── Horizontal bar chart ────────────────────────────────────────────────
//   <BarChart data={[{label:"Cash", value:1200}, ...]} valueFormat={n=>`GH₵ ${n}`} />
export function BarChart({ data = [], valueFormat = (n) => n, palette = BRAND_PALETTE }) {
  const max = Math.max(1, ...data.map(d => Number(d.value) || 0));
  return (
    <div className="bar-chart" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {data.length === 0 && (
        <div style={{ color: "#8a7060", fontSize: 13, fontStyle: "italic", padding: 12 }}>
          No data for this range.
        </div>
      )}
      {data.map((d, i) => {
        const pct = (Number(d.value) || 0) / max * 100;
        const colour = d.color || palette[i % palette.length];
        return (
          <div key={d.label || i}>
            <div style={{
              display: "flex", justifyContent: "space-between",
              fontSize: 12, color: "#3a2a1a", marginBottom: 3,
            }}>
              <span>{d.label}</span>
              <span style={{ fontWeight: 500 }}>{valueFormat(d.value)}</span>
            </div>
            <div style={{
              height: 10, background: "rgba(180,140,80,.12)",
              borderRadius: 4, overflow: "hidden",
            }}>
              <div style={{
                width: `${pct}%`, height: "100%", background: colour,
                transition: "width .4s ease",
              }}/>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Pie / donut chart ───────────────────────────────────────────────────
//   <PieChart data={[{label:"Available", value:8}, ...]} />
// Renders a donut (hole ratio 0.55) with a side legend.
export function PieChart({ data = [], size = 160, hole = 0.55, palette = BRAND_PALETTE }) {
  const total = data.reduce((s, d) => s + (Number(d.value) || 0), 0);
  const cx = size / 2, cy = size / 2, r = size / 2 - 2;
  const ir = r * hole;

  // Build pie slice paths.
  let acc = 0;
  const slices = data.map((d, i) => {
    const v   = Number(d.value) || 0;
    const pct = total > 0 ? v / total : 0;
    const a1  = acc * 2 * Math.PI - Math.PI / 2;
    const a2  = (acc + pct) * 2 * Math.PI - Math.PI / 2;
    acc += pct;
    const large = pct > 0.5 ? 1 : 0;
    const x1 = cx + r  * Math.cos(a1), y1 = cy + r  * Math.sin(a1);
    const x2 = cx + r  * Math.cos(a2), y2 = cy + r  * Math.sin(a2);
    const xi2 = cx + ir * Math.cos(a2), yi2 = cy + ir * Math.sin(a2);
    const xi1 = cx + ir * Math.cos(a1), yi1 = cy + ir * Math.sin(a1);
    const path = pct === 0 ? "" :
      pct >= 1 // single 100% slice — draw two arcs
        ? `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx - 0.01} ${cy - r} L ${cx - 0.01} ${cy - ir} A ${ir} ${ir} 0 1 0 ${cx} ${cy - ir} Z`
        : `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} L ${xi2} ${yi2} A ${ir} ${ir} 0 ${large} 0 ${xi1} ${yi1} Z`;
    return { ...d, pct, path, color: d.color || palette[i % palette.length] };
  });

  return (
    <div style={{ display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {total === 0 && (
          <>
            <circle cx={cx} cy={cy} r={r}  fill="rgba(180,140,80,.10)" />
            <circle cx={cx} cy={cy} r={ir} fill="#fff" />
          </>
        )}
        {slices.map((s, i) => s.path && (
          <path key={i} d={s.path} fill={s.color} stroke="#fff" strokeWidth="0.8"/>
        ))}
        <text x={cx} y={cy - 2} textAnchor="middle"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fill: "#2c1f14", fontWeight: 600 }}>
          {total}
        </text>
        <text x={cx} y={cy + 14} textAnchor="middle"
              style={{ fontSize: 9, letterSpacing: ".14em", textTransform: "uppercase", fill: "#6b5142" }}>
          total
        </text>
      </svg>
      <div className="pie-legend" style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 160 }}>
        {slices.map((s) => (
          <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color, flexShrink: 0 }}/>
            <span style={{ color: "#3a2a1a" }}>{s.label}</span>
            <span style={{ marginLeft: "auto", color: "#6b5142", fontWeight: 500 }}>
              {s.value}{total > 0 ? ` · ${Math.round(s.pct * 100)}%` : ""}
            </span>
          </div>
        ))}
        {slices.length === 0 && (
          <div style={{ color: "#8a7060", fontStyle: "italic", fontSize: 12.5 }}>
            No data for this range.
          </div>
        )}
      </div>
    </div>
  );
}

// ── Line chart (time series) ────────────────────────────────────────────
//   <LineChart data={[{label:"Mon", value:120}, ...]} valueFormat={n=>`GH₵ ${n}`} />
export function LineChart({
  data = [], valueFormat = (n) => n, height = 180,
  stroke = "#c8a05a", fill = "rgba(200,160,90,.18)",
}) {
  const W = 600, H = height, P = 24;
  const values = data.map(d => Number(d.value) || 0);
  const max = Math.max(1, ...values);
  const min = 0;
  const stepX = data.length > 1 ? (W - P * 2) / (data.length - 1) : 0;
  const y = (v) => H - P - ((v - min) / (max - min)) * (H - P * 2);

  const points = data.map((d, i) => [P + i * stepX, y(d.value || 0)]);
  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0]} ${p[1]}`).join(" ");
  const fillPath = points.length > 1
    ? `${linePath} L ${points[points.length - 1][0]} ${H - P} L ${points[0][0]} ${H - P} Z`
    : "";

  return (
    <div style={{ width: "100%" }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: "100%", height }}>
        {/* baseline + max grid lines */}
        <line x1={P} x2={W - P} y1={H - P} y2={H - P} stroke="rgba(180,140,80,.4)" strokeWidth="0.6"/>
        <line x1={P} x2={W - P} y1={P}    y2={P}     stroke="rgba(180,140,80,.15)" strokeWidth="0.6" strokeDasharray="3 4"/>

        {/* fill + line */}
        {data.length > 0 && (
          <>
            <path d={fillPath} fill={fill}/>
            <path d={linePath} fill="none" stroke={stroke} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round"/>
            {points.map(([x, y2], i) => (
              <circle key={i} cx={x} cy={y2} r="2.4" fill={stroke}/>
            ))}
          </>
        )}

        {/* min / max labels */}
        <text x={P} y={P - 4} fontSize="9" fill="#8a7060">{valueFormat(max)}</text>
        <text x={P} y={H - P + 12} fontSize="9" fill="#8a7060">0</text>
      </svg>
      {/* X-axis labels */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        padding: "0 24px", marginTop: 4,
        fontSize: 10.5, color: "#8a7060",
      }}>
        {data.length === 0 ? (
          <span style={{ color: "#8a7060", fontStyle: "italic" }}>No data for this range.</span>
        ) : data.map((d, i) => (
          // Only label every Nth point so the row doesn't crowd.
          (data.length <= 14 || i % Math.ceil(data.length / 10) === 0 || i === data.length - 1)
            ? <span key={i}>{d.label}</span>
            : <span key={i} aria-hidden="true">&nbsp;</span>
        ))}
      </div>
    </div>
  );
}
